import { useEffect, useState } from 'react';
import { Minus, Plus } from 'lucide-react';

const KEY = 'agio-font-scale';
const MIN = 90;
const MAX = 170;
const STEP = 10;
/** Matches the `font-size: 120%` fallback in styles.css — that CSS rule is
 * what a first-ever visitor (no localStorage yet) actually renders at, so the
 * control has to start there too or the first click would jump the page. */
const DEFAULT = 120;

function readScale(): number {
  try {
    const v = Number(localStorage.getItem(KEY));
    return v >= MIN && v <= MAX ? v : DEFAULT;
  } catch {
    return DEFAULT;
  }
}

/** Site-wide text/spacing size stepper. Every Tailwind text and spacing
 * utility is rem-based (see the html font-size comment in styles.css), so
 * changing the root font-size scales the whole UI at once — same mechanism
 * as the 120% baseline, just user-adjustable. An inline style on <html> wins
 * over the CSS rule without needing to touch it; index.html applies the saved
 * value before React mounts so there's no jump on load. */
export function TextSizeControl() {
  const [scale, setScale] = useState(readScale);

  useEffect(() => {
    document.documentElement.style.fontSize = `${scale}%`;
    try {
      localStorage.setItem(KEY, String(scale));
    } catch {
      // Private browsing / storage disabled — size just won't persist.
    }
  }, [scale]);

  return (
    <div className="flex items-center overflow-hidden rounded-md border border-border text-muted-foreground">
      <button
        type="button"
        onClick={() => setScale((s) => Math.max(MIN, s - STEP))}
        disabled={scale <= MIN}
        title="הקטן טקסט"
        aria-label="הקטן טקסט"
        className="flex h-8 w-8 items-center justify-center transition-colors hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <Minus className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => setScale(DEFAULT)}
        title="איפוס גודל טקסט"
        aria-label="איפוס גודל טקסט"
        className="flex h-8 items-center border-x border-border px-1.5 text-xs font-semibold transition-colors hover:bg-muted"
      >
        Aa
      </button>
      <button
        type="button"
        onClick={() => setScale((s) => Math.min(MAX, s + STEP))}
        disabled={scale >= MAX}
        title="הגדל טקסט"
        aria-label="הגדל טקסט"
        className="flex h-8 w-8 items-center justify-center transition-colors hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
